<template>
  <lp-grid
    container
    class="layout-container blueprint"
    :class="[
      !showLeftPanel && 'collapse-left',
      !showRightPanel && 'collapse-right',
    ]"
    gap="0"
    padding="0"
    bg="gray-light"
    no-gap
    v-bind="$attrs"
  >
    <transition name="drop-left">
      <lp-grid
        v-show="showLeftPanel"
        class="smart-panel-l"
        bg="white"
        gap="0"
        shadow
      >
        <slot name="panelLeft" />
      </lp-grid>
    </transition>
    <lp-grid class="panel-main" direction="col">
      <div v-if="$slots.header || $slots.nav">
        <slot name="header">
          <transition name="drop-bottom">
            <div
              v-show="showHeader"
              class="block-subheader bg-white shadow p-2"
            >
              Sub-header
            </div>
          </transition>
        </slot>
        <slot name="nav">
          <transition name="drop-bottom">
            <div v-show="showNav" class="block-tabs bg-white shadow p-2">
              Tabs
            </div>
          </transition>
        </slot>
      </div>
      <h3 v-if="title" class="mb-1">{{ title }}</h3>
      <slot name="default" />
    </lp-grid>
    <transition name="drop-left">
      <lp-grid
        v-show="showRightPanel"
        class="smart-panel-r"
        bg="white"
        gap="0"
        shadow
      >
        <slot name="panelRight" />
      </lp-grid>
    </transition>
  </lp-grid>
</template>
<script lang="ts">
export default {
  name: 'lp-blueprint',
  inheritAttrs: false,
}
</script>
<script setup lang="ts">
const emit = defineEmits(['update:modelValue'])

interface Props {
  showLeftPanel?: boolean
  showRightPanel?: boolean
  showHeader?: boolean
  showNav?: boolean
  title?: string
}

const props = withDefaults(defineProps<Props>(), {
  showLeftPanel: false,
  showRightPanel: false,
  showHeader: false,
  showNav: false,
})
</script>

<style lang="scss">
:root {
  --panel-right-width: 300px;
  --panel-left-width: 300px;
}
.blueprint {
  height: 100vh;
  background: #f2f3f3;
  transition: 0.3s ease-in all;
  display: grid;
  grid-template-columns: auto;
  grid-template-areas: 'panel-center';
  grid-gap: 0;
  overflow: hidden;

  &:has(.smart-panel-l) {
    grid-template-columns: var(--panel-left-width, 24rem) auto;
    grid-template-areas: 'panel-left panel-center';

    &.collapse-left {
      grid-template-columns: 3rem auto;
    }
  }

  &:has(.smart-panel-r) {
    grid-template-columns: auto var(--panel-right-width, 24rem) solid black;
    grid-template-areas: 'panel-center panel-right';

    &.collapse-right {
      grid-template-columns: auto 3rem;
    }
  }

  &:has(.smart-panel-l):has(.smart-panel-r) {
    grid-template-columns: var(--panel-left-width, 24rem) auto var(
        --panel-right-width,
        24rem
      );
    grid-template-areas: 'panel-left panel-center panel-right';

    &.collapse-left:not(.collapse-right) {
      grid-template-columns: 3rem auto var(--panel-right-width, 24rem);
    }

    &.collapse-right:not(.collapse-left) {
      grid-template-columns: var(--panel-left-width, 24rem) auto 3rem;
    }

    &.collapse-right.collapse-left {
      grid-template-columns: 3rem auto 3rem;
    }
  }

  .smart-panel-l {
    grid-area: panel-left;
  }

  .smart-panel-r {
    grid-area: panel-right;
  }

  .panel-main {
    grid-area: panel-center;
    z-index: 1;
    background: #dedede;
    overflow: auto;
  }

  // hide restore icon when panel is shown
  .icon-restore {
    display: none;
  }

  // hide everything but restore icon when panel is collapsed
  &.collapse-right {
    .smart-panel-r {
      .icon-restore {
        display: block !important;
      }

      & > :not(.icon-restore) {
        display: none;
      }
    }
  }

  &.collapse-left {
    .smart-panel-l {
      .icon-restore {
        display: block !important;
      }

      & > :not(.icon-restore) {
        display: none;
      }
    }
  }

  [class*='smart-panel-'] {
    background: #fff;
    position: relative;
    z-index: 4;
    overflow-x: hidden;
    overflow-y: auto;
    // @extend .shadow;
  }
}
</style>
